/**
 * B1.9 Invoice feature — mock↔live API dispatcher.
 *
 * Runtime switch driven by {@link APP_ENV.bizApiMode}:
 *   - `mock` (default): serves deterministic seed rows from
 *     {@link ./invoice-api-mock} (Lovable / Storybook / pre-wire sessions).
 *   - `live`: routes through {@link ./invoice-api-http} → `bizClient` →
 *     `/api/biz/invoices/*` on the backend.
 *
 * The dispatcher is resolved at module load so React Query cache keys and
 * hook signatures stay identical regardless of the mode. Flipping
 * `VITE_BIZ_API_MODE` (or `import.meta.env.VITE_BIZ_API_MODE` at build
 * time) is enough to graduate a feature from fixtures to real endpoints —
 * no hook rewrites, no downstream churn.
 *
 * Helpers that are intentionally *always* sourced from the mock module
 * (they are presentation-layer utilities with no I/O):
 *   - DEFAULT_LIST_FILTERS
 *   - buildIssueIdempotencyKey
 *   - statusSummary
 *
 * @see invoice-api-mock.ts
 * @see invoice-api-http.ts
 * @see docs/_context/PETOTA_BUSINESS_IMPLEMENTATION_PLAN.md §Faz 2.D — FE rewire
 */

import { APP_ENV } from '@/shared/config/env';

import * as mockApi from './invoice-api-mock';
import * as httpApi from './invoice-api-http';

import type {
  DownloadInvoiceRequest,
  InvoiceListFilters,
  InvoiceListSnapshot,
  InvoiceRow,
  IssueInvoiceRequest,
  ListInvoicesRequest,
  SendInvoiceRequest,
  VoidInvoiceRequest,
} from './types';

const useLive = APP_ENV.bizApiMode === 'live';

export async function listInvoices(
  req: ListInvoicesRequest,
): Promise<InvoiceListSnapshot> {
  return useLive ? httpApi.listInvoices(req) : mockApi.listInvoices(req);
}

export async function getInvoiceSnapshot(
  branchId: string,
  filters?: Partial<InvoiceListFilters>,
): Promise<InvoiceListSnapshot> {
  return useLive
    ? httpApi.getInvoiceSnapshot(branchId, filters)
    : mockApi.getInvoiceSnapshot(branchId, filters);
}

export async function issueInvoice(
  req: IssueInvoiceRequest,
): Promise<InvoiceRow> {
  return useLive ? httpApi.issueInvoice(req) : mockApi.issueInvoice(req);
}

export async function sendInvoice(
  req: SendInvoiceRequest,
): Promise<InvoiceRow> {
  return useLive ? httpApi.sendInvoice(req) : mockApi.sendInvoice(req);
}

export async function voidInvoice(
  req: VoidInvoiceRequest,
): Promise<InvoiceRow> {
  return useLive ? httpApi.voidInvoice(req) : mockApi.voidInvoice(req);
}

export async function downloadInvoice(
  req: DownloadInvoiceRequest,
): Promise<{ url: string; filename: string }> {
  return useLive ? httpApi.downloadInvoice(req) : mockApi.downloadInvoice(req);
}

// ---------------------------------------------------------------------------
// Presentation-layer helpers (never hit the network — always from mock)
// ---------------------------------------------------------------------------

export {
  DEFAULT_LIST_FILTERS,
  buildIssueIdempotencyKey,
  statusSummary,
} from './invoice-api-mock';

/**
 * Introspection helper — exposes the currently active mode so debug panels
 * and QA tooling can surface the dispatcher choice without re-reading env.
 */
export function getInvoiceApiMode(): 'mock' | 'live' {
  return useLive ? 'live' : 'mock';
}
